import styles from './CheckoutInfoPage.scss';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import NativeSelect from '@material-ui/core/NativeSelect';
import TextField from '@material-ui/core/TextField';
import { useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { geocodeByPlaceId } from 'react-places-autocomplete';
import { STATE_LIST } from 'src/components/Contact/Fields';
import { LocationSearchAutocomplete } from 'src/components/LocationSearchAutocomplete/LocationSearchAutocomplete';
import { useDesktopLarge } from 'src/hooks/UseResponsive';
import { GoogleStreetAddressModel } from '../models';

import { CheckoutInfoFields } from './CheckoutInfoPage';

const FORM_FIELD_IDS = {
  FIRST_NAME: 'first-name',
  LAST_NAME: 'last-name',
  BUSINESS_NAME: 'business-name',
  ADDRESS_LINE1: 'address-line1',
  ADDRESS_LINE2: 'address-line2',
  CITY: 'city',
  STATE: 'state',
  ZIP_CODE: 'zip-code',
};

export const FirstNameField = () => {
  const { t } = useTranslation();
  const { values, handleBlur, handleChange, touched, errors } = useFormikContext<CheckoutInfoFields>();

  return (
    <FormControl
      fullWidth
    >
      <TextField
        autoFocus
        id={FORM_FIELD_IDS.FIRST_NAME}
        inputProps={{
          'data-test': 'first-name',
        }}
        name="firstName"
        label={t('firstName.label')}
        value={values.firstName}
        onBlur={handleBlur}
        onChange={handleChange}
        helperText={touched.firstName && errors.firstName}
        error={!!(touched.firstName && errors.firstName)}
      />
    </FormControl>
  );
};

export const LastNameField = () => {
  const { t } = useTranslation();
  const { values, handleBlur, handleChange, touched, errors } = useFormikContext<CheckoutInfoFields>();

  return (
    <FormControl
      fullWidth
    >
      <TextField
        id={FORM_FIELD_IDS.LAST_NAME}
        inputProps={{
          'data-test': 'last-name',
        }}
        name="lastName"
        label={t('lastName.label')}
        value={values.lastName}
        onBlur={handleBlur}
        onChange={handleChange}
        helperText={touched.lastName && errors.lastName}
        error={!!(touched.lastName && errors.lastName)}
      />
    </FormControl>
  );
};

export const BusinessNameField = () => {
  const { t } = useTranslation();
  const { values, handleBlur, handleChange, touched, errors } = useFormikContext<CheckoutInfoFields>();

  return (
    <FormControl
      fullWidth
    >
      <TextField
        id={FORM_FIELD_IDS.BUSINESS_NAME}
        inputProps={{
          'data-test': 'business-name',
        }}
        name="businessName"
        label={t('businessName.label')}
        value={values.businessName}
        onBlur={handleBlur}
        onChange={handleChange}
        helperText={touched.businessName && errors.businessName}
        error={!!(touched.businessName && errors.businessName)}
      />
    </FormControl>
  );
};

export const AddressLine1Field = () => {
  const { t } = useTranslation();
  const { values, handleBlur, touched, errors, setFieldValue } = useFormikContext<CheckoutInfoFields>();

  const handleAddressSelect = async (_: string, placeId: string | null) => {
    if (!placeId) return;

    const result = await geocodeByPlaceId(placeId);
    if (!result.length) return;

    const address = new GoogleStreetAddressModel(result[0].address_components);
    Object.keys(address).map(field => {
      const value = address[field];
      if (value) setFieldValue(field, value);
    });
  };

  return (
    <LocationSearchAutocomplete
      id={FORM_FIELD_IDS.ADDRESS_LINE1}
      dataTest="address1"
      name="address1"
      label={t('address1.label')}
      value={values.address1}
      handleChange={value => setFieldValue('address1', value)}
      onBlur={handleBlur}
      helperText={touched.address1 && errors.address1}
      error={!!(touched.address1 && errors.address1)}
      handleAddressSelect={handleAddressSelect}
    />
  );
};

export const AddressLine2Field = () => {
  const { t } = useTranslation();
  const { values, handleBlur, handleChange, touched, errors } = useFormikContext<CheckoutInfoFields>();

  return (
    <FormControl
      fullWidth
    >
      <TextField
        id={FORM_FIELD_IDS.ADDRESS_LINE2}
        inputProps={{
          'data-test': 'address2',
        }}
        name="address2"
        label={t('address2.label')}
        onBlur={handleBlur}
        onChange={handleChange}
        value={values.address2}
        helperText={touched.address2 && errors.address2}
        error={!!(touched.address2 && errors.address2)}
      />
    </FormControl>
  );
};

export const CityField = () => {
  const { t } = useTranslation();
  const { values, handleBlur, handleChange, touched, errors } = useFormikContext<CheckoutInfoFields>();

  return (
    <FormControl
      fullWidth
    >
      <TextField
        id={FORM_FIELD_IDS.CITY}
        inputProps={{
          'data-test': 'city',
        }}
        name="city"
        label={t('city.label')}
        value={values.city}
        onBlur={handleBlur}
        onChange={handleChange}
        helperText={touched.city && errors.city}
        error={!!(touched.city && errors.city)}
      />
    </FormControl>
  );
};

export const StateField = () => {
  const { t } = useTranslation();
  const { values, handleBlur, handleChange, touched, errors } = useFormikContext<CheckoutInfoFields>();
  const isDesktopLarge = useDesktopLarge();
  const hasError = !!(touched.state && errors.state);

  return (
    <FormControl
      fullWidth
    >
      <InputLabel htmlFor="state" className={hasError ? 'Mui-error' : undefined}>{t('state.label')}</InputLabel>
      <NativeSelect
        id={FORM_FIELD_IDS.STATE}
        name="state"
        onBlur={handleBlur}
        value={values.state}
        onChange={handleChange}
        error={hasError}
        IconComponent={isDesktopLarge ? undefined : () => null}
      >
        <option aria-label="None" value="" data-test="state" />
        {STATE_LIST.map(state => <option value={state} key={state}>{state}</option>)}
      </NativeSelect>
      <FormHelperText error>{touched.state && errors.state}</FormHelperText>
    </FormControl>
  );
};

export const ZipCodeField = () => {
  const { t } = useTranslation();
  const { values, handleBlur, touched, errors, setFieldValue } = useFormikContext<CheckoutInfoFields>();

  return (
    <FormControl
      fullWidth
    >
      <TextField
        id={FORM_FIELD_IDS.ZIP_CODE}
        className={styles.checkoutInfoPageZipcode}
        name="zip"
        label={t('zip.label')}
        inputProps={{
          'data-test': 'zip',
        }}
        value={values.zip}
        type="number"
        onBlur={handleBlur}
        onChange={e => setFieldValue('zip', e.target.value)}
        helperText={touched.zip && errors.zip}
        error={!!(touched.zip && errors.zip)}
      />
    </FormControl>
  );
};
